import React, { useCallback, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Image, Typography, LoadingIndicator } from 'lib/components';
import InvoiceDetailDataTable from 'lib/components/DataTables/InvoiceDetail';
import InvoiceLogDataTable from 'lib/components/DataTables/InvoiceLog';
import ActionButton from './ActionButton';
import UnvoidInvoiceModal from './UnvoidInvoiceModal';
import VoidInvoiceModal from './VoidInvoiceModal';
import VoidInvoiceItemsModal from './VoidInvoiceItemsModal';
import {
	useAppDispatch,
	actions,
	useSelectInvoice,
	useSelectAuthorization,
	useSelectRequest,
	RequestManager,
	useSelectSettings,
} from 'store';
import { useComprehensiveTopNav, useAddIdPrefix, useCustomToast, useInvoiceDownload } from 'hooks';
import { InvoiceItemsAllCheckedStatus, Invoice } from 'types';
import routes from 'lib/api/routes';
import { getLocation } from 'lib/util';
import classes from './index.module.scss';
import PaymentStatusDropdown from './PaymentStatusDropdown';
import adjustTime from '../../lib/adjustTime';

/**
 * Props interface for the InvoiceDetailScreenView component
 */
interface Props {
	/** The invoice data to display */
	invoice: Invoice;
}

/**
 * InvoiceDetailScreen container component
 * Fetches invoice data and passes it to the view component
 * Shows a loading indicator while data is being fetched
 *
 * @returns {JSX.Element} - Rendered component
 */
const InvoiceDetailScreen = () => {
	// Get invoice ID from URL parameters
	const { id } = useParams<{ id: string }>();

	// Redux state and actions
	const dispatch = useAppDispatch();
	const { active } = useSelectRequest();
	const getInvoiceById = useSelectInvoice();

	// Local state for the invoice data
	const [invoice, setInvoice] = useState<Invoice>();

	/**
	 * Fetch invoice data on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchInvoice(parseInt(id)));
	}, []);

	/**
	 * Update local invoice state when data changes in Redux store
	 * Monitors active requests to detect when the fetch is complete
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.INVOICE_ID(parseInt(id)), 'GET')) {
			if (RM.succeeded()) {
				const invoice = getInvoiceById(parseInt(id));

				if (invoice) {
					setInvoice(invoice);
				}
			}
		}
	}, [active]);

	// Show loading indicator until invoice data is available
	return invoice ? <InvoiceDetailScreenView invoice={invoice} /> : <LoadingIndicator />;
};

/**
 * InvoiceDetailScreenView component
 * Main view component for displaying invoice details
 * Includes invoice information, item list, logs, and actions
 *
 * @param {Props} props - Component props containing invoice data
 * @returns {JSX.Element} - Rendered component
 */
const InvoiceDetailScreenView = ({ invoice }: Props) => {
	// Custom hooks for notifications and ID formatting
	const toast = useCustomToast();
	const addPrefix = useAddIdPrefix();

	// Redux state and actions
	const dispatch = useAppDispatch();
	const { active } = useSelectRequest();
	const settingsState = useSelectSettings();
	const { canVoidInvoice } = useSelectAuthorization();

	// Invoice void state
	const [voided, setVoided] = useState(invoice.reason ? true : false);

	// Modal visibility states
	const [voidInvoiceModalOpen, setVoidInvoiceModalOpen] = useState(false);
	const [voidInvoiceItemsModalOpen, setVoidInvoiceItemsModalOpen] = useState(false);
	const [unvoidInvoiceModalOpen, setUnvoidInvoiceModalOpen] = useState(false);

	// Item selection states
	const [checked, setChecked] = useState<number[]>([]);
	const [allChecked, setAllChecked] = useState(false);
	const [allCheckedStatus, setAllCheckedStatus] = useState<InvoiceItemsAllCheckedStatus>('none');

	/**
	 * Handle void/unvoid button click
	 * Opens the appropriate modal based on current void status
	 */
	const voidHandler = useCallback(() => {
		if (voided) {
			setUnvoidInvoiceModalOpen(true); // Open unvoid modal if already voided
		} else {
			setVoidInvoiceModalOpen(true); // Open void modal if not voided
		}
	}, [voided]);

	/**
	 * Monitor API requests for invoice actions
	 * Shows toast notifications for success/failure and updates UI state
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		// Handle void invoice request
		if (RM.req(routes.INVOICE_VOID(invoice.id), 'POST')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				setVoidInvoiceModalOpen(false);
				setVoided(true);
			}
		}

		// Handle void invoice items request
		if (RM.req(routes.INVOICE_VOID_ITEMS(invoice.id), 'POST')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				setVoidInvoiceItemsModalOpen(false);
				setAllCheckedStatus('none');
			}
		}

		// Handle unvoid invoice request
		if (RM.req(routes.INVOICE_UNVOID(invoice.id), 'POST')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				setVoidInvoiceModalOpen(false);
				setVoided(false);
			}
		}

		// Handle unvoid invoice items request
		if (RM.req(routes.INVOICE_UNVOID_ITEMS(invoice.id), 'POST')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				setAllCheckedStatus('none');
			}
		}

		// Handle send invoice PDF request
		if (RM.req(routes.INVOICE_SEND_ID(invoice.id), 'GET')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
			}
		}

		// Handle generate invoice PDF request
		if (RM.req(routes.INVOICE_GENERATE_ID(invoice.id), 'GET')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
			}
		}
	}, [active]);

	/**
	 * Update checked items based on allCheckedStatus
	 * Manages which items are selected based on the current selection mode
	 */
	useEffect(() => {
		// Skip if in partial selection mode
		if (allCheckedStatus === 'some') {
			return;
		}

		// Select all items
		if (allCheckedStatus === 'all') {
			setChecked(invoice.items.map(({ id }) => id));
		}

		// Deselect all items
		if (allCheckedStatus === 'none') {
			setChecked([]);
		}

		// Filter to only voided items
		if (allCheckedStatus === 'voided') {
			const voidedItemIds = invoice.items.filter((item) => item.isVoided).map(({ id }) => id);
			setChecked((prev) => prev.filter((item) => voidedItemIds.includes(item)));
		}

		// Filter to only unvoided items
		if (allCheckedStatus === 'unvoided') {
			const unvoidedItemIds = invoice.items.filter((item) => !item.isVoided).map(({ id }) => id);
			setChecked((prev) => prev.filter((item) => unvoidedItemIds.includes(item)));
		}
	}, [allCheckedStatus]);

	/**
	 * Handle individual item checkbox changes
	 * Updates the checked items array and selection mode
	 *
	 * @param {number} id - ID of the item being checked/unchecked
	 * @param {boolean} isChecked - Whether the item is being checked or unchecked
	 * @param {boolean} isVoided - Whether the item is voided
	 */
	const handleCheckAction = (id: number, isChecked: boolean, isVoided: boolean) => {
		// Update selection mode based on item void status
		if (allCheckedStatus !== 'some') {
			setAllChecked(false);
			setAllCheckedStatus(isVoided ? 'voided' : 'unvoided');
		}

		// Add item to checked array if being checked
		if (isChecked && !checked.includes(id)) {
			setChecked((prev) => [...prev, id]);
			return;
		}

		// Remove item from checked array if being unchecked
		if (!isChecked && checked.includes(id)) {
			setChecked((prev) => prev.filter((item) => item !== id));
		}
	};

	/**
	 * Handle "select all" checkbox changes
	 * Updates the selection mode and checked items
	 *
	 * @param {React.ChangeEvent<HTMLInputElement>} e - Change event
	 */
	const handleAllCheckAction = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		if (checked) {
			// Select all items
			setAllChecked(checked);
			setAllCheckedStatus('all');
		} else {
			// Deselect all items
			setAllChecked(checked);
			setAllCheckedStatus('none');
		}
	};

	/**
	 * Send invoice PDF by email
	 * Dispatches the sendPDF action
	 */
	const handleInvoiceSend = useCallback(() => {
		dispatch(actions.sendPDF(invoice.id));
	}, []);

	/**
	 * Generate invoice as PDF
	 * Dispatches the generatePDF action
	 */
	const handleInvoiceGenerate = useCallback(() => {
		dispatch(actions.generatePDF(invoice.id));
	}, []);

	/**
	 * Void the entire invoice
	 * Dispatches the voidInvoice action with the provided reason
	 *
	 * @param {string} reason - Reason for voiding the invoice
	 */
	const handleInvoiceVoid = useCallback((reason: string) => {
		dispatch(actions.voidInvoice(invoice.id, reason));
	}, []);

	/**
	 * Void selected invoice items
	 * Dispatches the voidInvoiceItems action with the provided reason and selected item IDs
	 *
	 * @param {string} reason - Reason for voiding the items
	 */
	const handleItemsVoid = useCallback(
		(reason: string) => {
			dispatch(actions.voidInvoiceItems(invoice.id, { reason, ids: checked }));
		},
		[checked]
	);

	/**
	 * Unvoid the entire invoice
	 * Dispatches the unvoidInvoice action
	 */
	const handleInvoiceUnvoid = useCallback(() => {
		dispatch(actions.unvoidInvoice(invoice.id));
	}, []);

	/**
	 * Unvoid selected invoice items
	 * Dispatches the unvoidInvoiceItems action with the selected item IDs
	 */
	const handleUnvoidItems = useCallback(() => {
		dispatch(actions.unvoidInvoiceItems(invoice.id, { ids: checked }));
	}, [checked]);

	// Set up invoice download functionality
	useInvoiceDownload();

	// Set up top navigation with invoice ID
	useComprehensiveTopNav(`Invoice - ${addPrefix(invoice.id)}`);

	return (
		<>
			{/* Modal for voiding the entire invoice */}
			<VoidInvoiceModal
				isOpen={voidInvoiceModalOpen}
				onClose={() => setVoidInvoiceModalOpen(false)}
				onVoidInvoice={handleInvoiceVoid}
			/>
			{/* Modal for voiding selected invoice items */}
			<VoidInvoiceItemsModal
				isOpen={voidInvoiceItemsModalOpen}
				onClose={() => setVoidInvoiceItemsModalOpen(false)}
				onVoidInvoiceItems={handleItemsVoid}
			/>
			{/* Debug logging - should be removed in production */}
			{console.log({ checked, allCheckedStatus })}
			{/* Modal for unvoiding the entire invoice */}
			<UnvoidInvoiceModal
				isOpen={unvoidInvoiceModalOpen}
				reason={invoice.reason}
				onClose={() => setUnvoidInvoiceModalOpen(false)}
				onUnvoidInvoice={handleInvoiceUnvoid}
			/>

			{/* Main invoice detail container */}
			<div className={classes['invoiceDetail']}>
				{/* Top section with invoice information */}
				<div className={classes['invoiceDetail-top']}>
					{/* Invoice information group */}
					<div className={classes['invoiceDetail-top-infoGroup']}>
						{/* Hospital name */}
						<div className={classes['infoGroupItem']}>
							<div className={classes['infoGroupItem-label']}>
								<div className={classes['infoGroupItem-labelIcon']}>
									<Image src="icPlusPrimary" />
								</div>
								<Typography className={classes['infoGroupItem-labelTitle']}>Hospital Name</Typography>
							</div>
							<Typography weight="bold">{invoice.hospital!.name}</Typography>
						</div>
						<div className={classes['infoGroupItem-separator']} />

						{/* Invoice creation date */}
						<div className={classes['infoGroupItem']}>
							<div className={classes['infoGroupItem-label']}>
								<div className={classes['infoGroupItem-labelIcon']}>
									<Image src="icClockPrimary" />
								</div>
								<Typography className={classes['infoGroupItem-labelTitle']}>
									Invoice Created Date
								</Typography>
							</div>
							<Typography weight="bold">
								{adjustTime(settingsState, invoice.createdAt, 'MM/DD/YYYY hh:mm A').format(
									'MM/DD/YYYY hh:mm A'
								)}
							</Typography>
						</div>
						<div className={classes['infoGroupItem-separator']} />

						{/* Hospital location */}
						<div className={classes['infoGroupItem']}>
							<div className={classes['infoGroupItem-label']}>
								<div className={classes['infoGroupItem-labelIcon']}>
									<Image src="icLocation" />
								</div>
								<Typography className={classes['infoGroupItem-labelTitle']}>
									Hospital Location
								</Typography>
							</div>
							<Typography weight="bold">{getLocation(invoice?.hospital?.address)}</Typography>
						</div>
					</div>

					{/* Action buttons */}
					<div className={classes['invoiceDetail-top-actions']}>
						{/* Payment status dropdown */}
						<PaymentStatusDropdown
							currentStatus={invoice.paymentStatus}
							onSelectStatus={(status) => console.log(status)}
						/>
						{/* Void/unvoid button (only shown if user has permission) */}
						{canVoidInvoice && (
							<ActionButton
								onClick={voidHandler}
								title={voided ? 'Job Voided' : 'Void Invoice'}
								status={voided ? 'danger' : 'primary'}
								leftIconSrc="icDangerAlert"
							/>
						)}
					</div>
				</div>
				{/* Main content section */}
				<div className={classes['invoiceDetail-content']}>
					{/* Invoice details section */}
					<div className={classes['invoiceDetail-details']}>
						<Typography variant="h5" weight="bold">
							Invoice Details
						</Typography>

						{/* Conditional action buttons for selected items */}
						{allCheckedStatus !== 'none' && checked.length !== 0 && (
							<>
								{/* Show void items button when unvoided items are selected */}
								{allCheckedStatus === 'unvoided' && (
									<div className={classes['invoiceDetail-details-actions']}>
										{canVoidInvoice && (
											<a
												style={{ cursor: 'pointer' }}
												onClick={() => setVoidInvoiceItemsModalOpen(true)}
											>
												<Typography variant="h6">Void Items</Typography>
											</a>
										)}
									</div>
								)}
								{/* Show unvoid items button when voided items are selected */}
								{allCheckedStatus === 'voided' && (
									<div className={classes['invoiceDetail-details-actions']}>
										{canVoidInvoice && (
											<a style={{ cursor: 'pointer' }} onClick={handleUnvoidItems}>
												<Typography variant="h6">Unvoid Items</Typography>
											</a>
										)}
									</div>
								)}
							</>
						)}

						{/* Invoice items table */}
						<div className={classes['invoiceDetail-details-table']}>
							<InvoiceDetailDataTable
								invoice={invoice}
								allChecked={allChecked}
								allCheckedStatus={allCheckedStatus}
								handleAllCheckAction={handleAllCheckAction}
								checkAction={handleCheckAction}
							/>
						</div>
					</div>

					{/* Logs section */}
					<div className={classes['invoiceDetail-logs']}>
						<Typography variant="h5" weight="bold">
							Logs
						</Typography>
						<div className={classes['invoiceDetail-logs-table']}>
							<InvoiceLogDataTable logs={invoice.logs} />
						</div>
					</div>
				</div>

				{/* Footer with PDF actions */}
				<div className={classes['invoiceDetail-foot']}>
					{/* Email PDF button */}
					<Button
						className={classes['btn']}
						title="Sent PDF by Email"
						variant="standard"
						onClick={handleInvoiceSend}
					/>
					{/* Generate PDF button */}
					<Button
						className={classes['btn']}
						title="Generate as PDF"
						variant="standard"
						onClick={handleInvoiceGenerate}
					/>
				</div>
			</div>
		</>
	);
};

export default InvoiceDetailScreen;
